import { useLayoutEffect, useRef, useState } from 'react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Avatar } from '../../../avatar';
import { Button } from '../../../button';
import { ImagePreview, useImagePreviewModalControl } from '../../../image-preview';
import { SkeletonLoader } from '../../../loader';
import { Modal, useModalControl } from '../../../modal';
import { Text } from '../../../text';
import { useChatBubbleContext } from '../../chat';
import { ChatStyles } from '../../chat.styles';

const IMAGE_SKELETON_SIZE = {
  width: 'min(70vw, 320px)',
  height: 'min(40vh, 480px)',
} as const;

type ImageBubbleProps = {
  src: string;
  suppressPreview?: boolean;
  onLoad?: () => void;
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  onClickDownload?: () => void;
  downloadImageTrackingId?: string;
};
export const ImageBubble = ({
  src,
  suppressPreview,
  onLoad,
  onError,
  onClickDownload,
  downloadImageTrackingId,
}: ImageBubbleProps) => {
  const [error, setError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const suppressOnLoadRef = useRef(false);
  const previewModalProps = useModalControl();
  const { triggerProps, modalProps: imagePreviewModalProps } = useImagePreviewModalControl();
  const { senderName, timestamp } = useChatBubbleContext();

  useLayoutEffect(() => {
    setError(false);
    setIsLoaded(false);

    const img = imgRef.current;
    // Avoid retriggering parent onLoad side effects in case the blob URL has been decoded already
    if (img?.complete && img.naturalWidth > 0) {
      suppressOnLoadRef.current = true;
      setIsLoaded(true);
      return;
    }

    suppressOnLoadRef.current = false;
  }, [src]);

  return (
    <>
      <figure {...(suppressPreview && src ? {} : triggerProps)} css={{ margin: 0 }}>
        {!error && !isLoaded && (
          <SkeletonLoader
            width={IMAGE_SKELETON_SIZE.width}
            height={IMAGE_SKELETON_SIZE.height}
            animation='shimmer'
            data-testid='chat-image-skeleton-loader'
          />
        )}
        {src && !error && (
          <img
            ref={imgRef}
            className='img-bubble'
            src={src}
            onLoad={() => {
              setIsLoaded(true);

              if (suppressOnLoadRef.current) {
                suppressOnLoadRef.current = false;
                return;
              }

              onLoad?.();
            }}
            onError={(e) => {
              setError(true);
              suppressOnLoadRef.current = false;
              onError?.(e);
            }}
            css={{
              cursor: !suppressPreview ? 'pointer' : 'default',
              display: isLoaded ? 'block' : 'none',
            }}
          />
        )}
        {error && (
          <>
            <Icon name='warning-badge' color='error' />
            <Text>There was an error loading this image.</Text>
          </>
        )}
      </figure>
      {src && (
        <PreviewModal
          src={src}
          previewModalProps={previewModalProps}
          onClickDownload={onClickDownload}
          downloadImageTrackingId={downloadImageTrackingId}
          imagePreviewModalProps={imagePreviewModalProps}
          senderName={senderName}
          timestamp={timestamp}
        />
      )}
    </>
  );
};

type PreviewModalProps = {
  src: string;
  previewModalProps: ReturnType<typeof useModalControl>;
  onClickDownload?: () => void;
  downloadImageTrackingId?: string;
  imagePreviewModalProps: ReturnType<typeof useImagePreviewModalControl>['modalProps'];
  senderName?: string;
  timestamp?: string | Date;
};
export const PreviewModal = ({
  src,
  previewModalProps,
  onClickDownload,
  downloadImageTrackingId,
  imagePreviewModalProps,
  senderName,
  timestamp,
}: PreviewModalProps) => {
  const modalStyles = ChatStyles.previewModal;

  //Trying the new ImagePreview Modal, will add a wetag to switch
  return (
    <ImagePreview src={src} alt='Image Preview' onClose={imagePreviewModalProps.onClose}>
      <ImagePreview.Modal
        {...imagePreviewModalProps}
        heading={
          <div css={{ display: 'flex', alignItems: 'center' }}>
            <Avatar name={senderName} size='small' />
            <div css={{ display: 'flex', flexDirection: 'column', marginLeft: theme.spacing(1) }}>
              <Text color='white' size='small' css={{ display: 'flex' }}>
                {senderName}
              </Text>
              <Text color='white' size='small'>
                {timestamp?.toString()}
              </Text>
            </div>
          </div>
        }
      >
        <ImagePreview.Body>
          <ImagePreview.ImageContent />
        </ImagePreview.Body>
        <ImagePreview.Controls />
      </ImagePreview.Modal>
    </ImagePreview>
  );
  return (
    <Modal css={modalStyles} {...previewModalProps.modalProps}>
      <div className='btn-containers'>
        {onClickDownload && (
          <Button
            onClick={onClickDownload}
            iconName='download'
            variant='secondary'
            className='download-button'
            {...(downloadImageTrackingId && { trackingId: downloadImageTrackingId })}
          />
        )}
        <Button
          size='small'
          onClick={previewModalProps.closeModal}
          className='close-button'
          variant='secondary'
          iconName='x'
        />
      </div>
      <Modal.Body>
        <img
          src={src}
          css={{
            maxWidth: '70vw',
            maxHeight: '90vh',
            padding: 0,
          }}
        />
      </Modal.Body>
    </Modal>
  );
};
